import React from "react";
import {
  AlertTriangle,
  CircleHelp,
  ChevronRight,
  Clock3,
  History,
  LoaderCircle,
  RefreshCcw,
  Save,
  SlidersHorizontal,
} from "lucide-react";
import useAccess from "../hooks/useAccess.jsx";
import { canManageSystemDiagnostics } from "../lib/accessControl.js";
import { useUserUiPreferences } from "../hooks/useUserUiPreferences.js";
import { invalidatePageDataCacheEntries } from "../hooks/usePageData.js";
import { invalidateShellDataCache } from "../hooks/useShellData.js";
import {
  fetchSystemConfigState,
  updateSystemConfigState,
} from "../lib/systemConfigApi.js";
import {
  buildDraftFromState,
  buildUpdatePayload,
  groupSettingsByGroup,
  groupSettingsBySection,
  toTimeSettingInputValue,
} from "../lib/systemConfigPageModel.js";
import { resolvePageSurfaceWidthClassName } from "../utils/pageSurfaceLayout.js";
import { dispatchPlanningStateChange } from "../utils/planningCrossViewSync.js";
import { refreshActiveDispatchWithToast } from "../utils/dispatchDayRefresh.js";
import { HoverCard, useToast } from "./ResultsPrimitives.jsx";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import SystemDiagnosticsSection from "./SystemDiagnosticsSection.jsx";
import {
  buttonSizeClasses,
  buttonToneClasses,
  joinClasses,
  pillToneClasses,
  sectionTagClassName,
  statusToneClasses,
} from "./resultsPrimitives/shared.js";
import "./SystemConfigPage.css";

const SYSTEM_CONFIG_SURFACE_WIDTH_CLASS_NAME = resolvePageSurfaceWidthClassName("system_config");
const PRE_ANCHOR_BUNDLE_GROUP_KEY = "pre_anchor_bundle";
const PRIORITY_TIME_WINDOWS_GROUP_KEY = "priority_time_windows";
const STRICT_P2_P3_TIME_WINDOWS_SETTING_KEY = "enforce_p2_p3_time_windows_as_hard";
const TRAFFIC_ROUTING_BUNDLE_GROUP_KEY = "traffic_routing_bundle";
const SOLLIO_COMMENT_AM_ENABLED_SETTING_KEY = "sollio_comment_am_enabled";
const SOLLIO_COMMENT_AM_UNLOADING_START_HOUR_SETTING_KEY = "sollio_comment_am_unloading_start_hour";
const DISPATCH_PERIOD_TOGGLE_ENABLED_SETTING_KEY = "dispatch_period_toggle_enabled";
// Temps fixes, débit d'aspiration et pause dîner : la journée active du Dispatch est actualisée tout de suite après l'enregistrement.
const FIXED_TIME_SETTING_KEYS = new Set(["loading_fixed_minutes", "delivery_fixed_minutes", "trip_fixed_minutes", "aspirator_rate_tons_per_hour", "lunch_break_minutes", "lunch_break_default_time"]);
// Heures par défaut des chauffeurs : le serveur ne marque que les dates ouvertes qui les lisent (domaine flotte).
const DRIVER_DEFAULT_HOURS_SETTING_KEYS = new Set([
  "driver_default_day_start_time",
  "driver_default_day_max_work_hours",
  "driver_default_evening_start_time",
  "driver_default_evening_max_work_hours",
]);
// Mêmes réglages que le serveur (RUNTIME_AFFECTING_SYSTEM_SETTING_KEYS de system_config.py) : ils passent le dispatch à revalider.
const RUNTIME_AFFECTING_SYSTEM_SETTING_KEYS = new Set([
  STRICT_P2_P3_TIME_WINDOWS_SETTING_KEY,
  "driver_early_start_flex_minutes",
  "here_time_aware_routing_enabled",
  "implicit_preload_companion_promotion_enabled",
  "max_loadings_per_truck_per_day",
  "near_delivery_probe_max_distance_km",
  "near_loading_probe_max_distance_km",
  "same_od_grouping_indivisible_enabled",
  "traffic_delta_amplification_enabled",
  "traffic_delta_multiplier",
]);
const TRAFFIC_ROUTING_GROUP_KEYS = new Set(["here_routing", "traffic_routing"]);
const PRE_ANCHOR_GROUP_KEYS = new Set(["pre_anchor_bonus", "pre_anchor_scope"]);
const PRE_ANCHOR_SETTING_ORDER = {
  pre_anchor_fill_bonus_per_minute_p1: 1,
  pre_anchor_fill_bonus_per_minute_p2: 2,
  pre_anchor_fill_bonus_per_minute_p3: 3,
  apply_pre_anchor_fill_to_loading: 4,
  apply_pre_anchor_fill_to_unloading: 5,
};
const SOLVER_COMPACT_GROUP_KEYS = new Set(["truck_activation", "driver_cost", "solver_determinism"]);
const TRUCK_WORK_TIME_SUBGROUPS = [
  {
    title: "Horaires par défaut des chauffeurs",
    keys: ["driver_default_day_start_time", "driver_default_day_max_work_hours", "driver_default_evening_start_time", "driver_default_evening_max_work_hours"],
  },
  {
    title: "Marges et flexibilité",
    keys: ["max_work_time_flex_minutes", "driver_early_start_flex_minutes", "manual_mix_inter_product_buffer_minutes"],
  },
  {
    title: "Temps fixes",
    keys: ["delivery_fixed_minutes", "loading_fixed_minutes", "trip_fixed_minutes"],
  },
  {
    title: "Aspiration",
    keys: ["aspirator_rate_tons_per_hour"],
  },
  {
    title: "Pause dîner",
    keys: ["lunch_break_minutes", "lunch_break_default_time"],
  },
];
const ROUTE_CONTINUITY_GROUP_KEY = "route_continuity";
const SETTING_GROUP_HEADER_TITLE_CLASS_NAME = "m-0 text-[0.76rem] font-bold uppercase tracking-[0.12em] text-slate-900";
const SETTING_GROUP_HEADER_TITLE_COMPACT_CLASS_NAME = "m-0 text-[0.74rem] font-bold uppercase tracking-[0.12em] text-slate-900";
const SETTING_VALUE_ACTIVE_LABEL_CLASS_NAME = "text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-slate-700";
const SETTING_VALUE_ACTIVE_LABEL_COMPACT_CLASS_NAME = "text-[0.64rem] font-semibold uppercase tracking-[0.12em] text-slate-700";
const SETTING_GROUP_HEADER_SURFACE_CLASS_NAME = "rounded-[4px] border border-slate-300 bg-slate-200 px-3 py-2";
const SETTING_GROUP_HEADER_SURFACE_GRID_CLASS_NAME = joinClasses([
  SETTING_GROUP_HEADER_SURFACE_CLASS_NAME,
  "grid gap-0.5",
]);
const SETTING_GROUP_HEADER_SURFACE_FLEX_CLASS_NAME = joinClasses([
  SETTING_GROUP_HEADER_SURFACE_CLASS_NAME,
  "flex items-start justify-between gap-3",
]);
const SYSTEM_CONFIG_SECTION_ID_PREFIX = "system-config-section-";
const SYSTEM_CONFIG_SECTION_SCROLL_MARGIN_CLASS_NAME = "scroll-mt-44";
const SYSTEM_CONFIG_SECTION_TAG_CLASS_NAME = joinClasses([sectionTagClassName, "rounded-[4px]!"]);
const GENERAL_SETTING_SECTION_KEYS = new Set(["dispatch_interface", "estimation_costs", "deployment_update"]);

const SECTION_META = {
  deployment_update: {
    badge: "Global",
    title: "Mise à jour du SaaS",
  },
  dispatch_interface: {
    badge: "Dispatch",
    title: "Interface Dispatch",
  },
  estimation_costs: {
    badge: "Moteur",
  },
  order_import_defaults: {
    badge: "Imports Excel",
    title: "Imports fichier",
    description: "Ces réglages complètent les lignes Excel au moment de l’import quand la colonne concernée n’impose pas déjà sa propre valeur.",
  },
  solver_settings: {
    badge: "Solveur",
    title: "Réglages solveur",
    description: "Ces paramètres couvrent les coûts et contraintes solveur hors priorités: ouverture camion, départ plus tôt, chargements journaliers et regroupement chargement-livraison.",
  },
};

const SETTING_GROUP_META = {
  dispatch_period_toggle: {
    title: "Sélecteur de période",
    description: "Affiche ou masque le sélecteur Jour / Soir dans Dispatch. Le dispatch Soir et le bouton distinct Afficher/Masquer le soir restent disponibles.",
  },
  truck_activation: {
    title: "Activation camion",
    description: "Coût fixe ajouté lorsqu’un camion est effectivement ouvert par l’optimisation.",
  },
  driver_cost: {
    title: "Coût chauffeur",
    description: "Taux horaire moyen d’un chauffeur, avantages inclus. Il complète les taux $/km et $/min des camions dans le coût réel et dans les choix de l’optimiseur.",
  },
  truck_work_time: {
    title: "Budget de travail camion",
    description: "Heures par défaut des chauffeurs, puis règles temporelles appliquées au début et aux heures max de chaque remorque.",
  },
  truck_loading_limits: {
    title: "Limite de chargements",
    description: "Borne dure sur le nombre de visites de chargement réellement construites par camion pour une journée. Les commandes d’aspiration sont exclues; une visite mixte avec une commande ordinaire reste comptée une fois.",
  },
  loading_client_default_grain_types: {
    title: "Produit par client de chargement",
    description: "Cochez un client puis renseignez le produit à injecter quand la colonne Produit du fichier Excel est vide.",
  },
  sollio_comment_am_defaults: {
    title: "Règle Sollio am",
    description: "Quand la colonne 'commentaire' du fichier Sollio contient la chaîne 'am', ces valeurs remplissent Début déchargement et Priorité déchargement dans le fichier converti.",
  },
  [PRE_ANCHOR_BUNDLE_GROUP_KEY]: {
    title: "Bonus pré-ancre et portée",
    description: "Ces 5 réglages sont lus ensemble pour arbitrer les minutes utiles avant une commande ancre lors de l’assignation sur camion.",
  },
  pre_anchor_bonus: {
    title: "Bonus pré-ancre par priorité",
    description: "Pondération par minute gagnée avant l’arrivée à l’ancre. Le trajet avant l’ancre compte; la minute bonusée s’arrête à l’arrivée.",
  },
  pre_anchor_scope: {
    title: "Portée du bonus pré-ancre",
    description: "Contrôle les familles d’étapes où le bonus pré-ancre peut s’appliquer. Chargement et déchargement sont pilotés séparément.",
  },
  same_od_pair_grouping: {
    title: "Regroupement même chargement-livraison",
    description: "Règles de regroupement appliquées quand le site de chargement, le produit et la destination exacte ou voisine rendent une consolidation pertinente.",
  },
  [ROUTE_CONTINUITY_GROUP_KEY]: {
    title: "Roulage peu chargé",
    description: "Définit ce qui compte comme un trajet peu chargé et l’importance donnée à cette préférence dans le choix initial des camions.",
  },
  here_routing: {
    title: "Trafic et horaire HERE",
    description: "Tient compte de l’heure de départ de chaque trajet.",
  },
  traffic_routing: {
    title: "Trafic / Routage",
    description: "Amplifie uniquement le delta entre la durée HERE sans trafic et la durée HERE avec trafic. Le total n’est jamais multiplié directement.",
  },
  [TRAFFIC_ROUTING_BUNDLE_GROUP_KEY]: {
    title: "Trafic / Routage",
    description: "Trafic HERE et son amplification.",
  },
  fleet_segment_hovercard: {
    title: "Infobulle segments Vue Flotte",
    description: "Ajoute un comparatif HERE à la demande dans l’infobulle des segments. Quand le trafic HERE est actif, l’infobulle compare HERE avec trafic à HERE sans trafic.",
  },
};

const BOOLEAN_VALUE_LABELS = {
  true: "Activé",
  false: "Désactivé",
};

const FEEDBACK_TONE_CLASS_NAMES = {
  danger: statusToneClasses.danger,
  info: statusToneClasses.info,
  success: statusToneClasses.success,
};

const PRIMARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-[4px] border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  buttonToneClasses.primary,
]);
const SECONDARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-[4px] border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  buttonToneClasses.ghost,
]);

function resolveSystemConfigSectionId(sectionKey) {
  return `${SYSTEM_CONFIG_SECTION_ID_PREFIX}${sectionKey}`;
}

function SettingSectionJumpNav({ items }) {
  if (!Array.isArray(items) || items.length < 2) {
    return null;
  }

  return (
    <nav aria-label="Accès rapide aux sections des paramètres système" className="flex min-w-0 flex-[1_1_40rem] flex-wrap items-center gap-1.5">
      <span className="self-center px-1 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Aller à</span>
      {items.map((item) => (
        <a
          className="inline-flex h-7 items-center rounded-[4px] border border-slate-200 bg-white px-2.5 text-[0.7rem] font-semibold text-slate-700 transition hover:border-accent/40 hover:text-accent-strong"
          href={`#${resolveSystemConfigSectionId(item.key)}`}
          key={item.key}
        >
          {item.title}
        </a>
      ))}
    </nav>
  );
}

function formatNumberValue(value, maximumFractionDigits = 2) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "—";
  }

  return new Intl.NumberFormat("fr-CA", {
    maximumFractionDigits,
    minimumFractionDigits: 0,
  }).format(numericValue);
}

function formatHistoryDate(value) {
  if (!value) {
    return "Date indisponible";
  }

  const parsedDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "Date indisponible";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsedDate);
}

function resolveFeedbackToneClassName(tone) {
  return FEEDBACK_TONE_CLASS_NAMES[tone] || statusToneClasses.neutral;
}

function resolveGroupedSettingLabel(setting) {
  switch (setting?.key) {
    case "pre_anchor_fill_bonus_per_minute_p1":
      return "P1";
    case "pre_anchor_fill_bonus_per_minute_p2":
      return "P2";
    case "pre_anchor_fill_bonus_per_minute_p3":
      return "P3";
    case "apply_pre_anchor_fill_to_loading":
      return "Chargement";
    case "apply_pre_anchor_fill_to_unloading":
      return "Déchargement";
    default:
      return setting?.label || "Paramètre";
  }
}

function formatHourInputBound(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return undefined;
  }

  const totalMinutes = Math.round(numericValue * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function resolveSettingDraftFallbackValue(setting) {
  if (setting?.value_type === "boolean") {
    return false;
  }
  if (setting?.value_type === "client_product_pairs") {
    return [];
  }
  if (setting?.value_type === "time") {
    return "";
  }
  return "";
}

function canEditSystemConfigSetting(setting, canEdit) {
  if (!canEdit || !setting || setting.is_read_only) {
    return false;
  }
  if (typeof setting.can_edit === "boolean") {
    return setting.can_edit;
  }
  return true;
}

function canEditSystemConfigTolerance(tolerance, canEdit) {
  if (!canEdit || !tolerance) {
    return false;
  }
  if (typeof tolerance.can_edit === "boolean") {
    return tolerance.can_edit;
  }
  return true;
}

function normalizeStringListSettingValue(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  const resolvedValues = [];
  const seenValues = new Set();
  value.forEach((item) => {
    if (typeof item !== "string") {
      return;
    }

    const resolvedItem = item.trim();
    if (!resolvedItem) {
      return;
    }

    const lookupKey = resolvedItem.toLocaleLowerCase("fr-CA");
    if (seenValues.has(lookupKey)) {
      return;
    }

    seenValues.add(lookupKey);
    resolvedValues.push(resolvedItem);
  });

  return resolvedValues.sort((left, right) => left.localeCompare(right, "fr-CA", { sensitivity: "base" }));
}

function normalizeClientProductPairsDraftValue(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  const resolvedPairsByLookupKey = new Map();
  value.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }

    const clientName = typeof item.client_name === "string" ? item.client_name.trim() : "";
    if (!clientName) {
      return;
    }

    resolvedPairsByLookupKey.set(clientName.toLocaleLowerCase("fr-CA"), {
      client_name: clientName,
      grain_type: typeof item.grain_type === "string" ? item.grain_type : "",
    });
  });

  return Array.from(resolvedPairsByLookupKey.values()).sort((left, right) => (
    left.client_name.localeCompare(right.client_name, "fr-CA", { sensitivity: "base" })
  ));
}

function resolveClientProductPair(currentValue, clientName) {
  const normalizedClientName = clientName.trim().toLocaleLowerCase("fr-CA");
  return normalizeClientProductPairsDraftValue(currentValue).find(
    (pair) => pair.client_name.toLocaleLowerCase("fr-CA") === normalizedClientName,
  ) || null;
}

function toggleClientProductPairSettingValue(currentValue, clientName, isChecked) {
  const normalizedClientName = clientName.trim().toLocaleLowerCase("fr-CA");
  const nextPairs = normalizeClientProductPairsDraftValue(currentValue).filter(
    (pair) => pair.client_name.toLocaleLowerCase("fr-CA") !== normalizedClientName,
  );
  if (isChecked) {
    nextPairs.push({
      client_name: clientName,
      grain_type: resolveClientProductPair(currentValue, clientName)?.grain_type || "",
    });
  }
  return nextPairs.sort((left, right) => left.client_name.localeCompare(right.client_name, "fr-CA", { sensitivity: "base" }));
}

function updateClientProductPairSettingGrainType(currentValue, clientName, grainType) {
  const existingPair = resolveClientProductPair(currentValue, clientName);
  const nextPairs = normalizeClientProductPairsDraftValue(currentValue).filter(
    (pair) => pair.client_name.toLocaleLowerCase("fr-CA") !== clientName.trim().toLocaleLowerCase("fr-CA"),
  );

  nextPairs.push({
    client_name: existingPair?.client_name || clientName,
    grain_type: grainType,
  });

  return nextPairs.sort((left, right) => left.client_name.localeCompare(right.client_name, "fr-CA", { sensitivity: "base" }));
}

function countCompletedClientProductPairs(value) {
  return normalizeClientProductPairsDraftValue(value).filter((pair) => pair.grain_type.trim()).length;
}

function countSelectedClientProductPairs(value) {
  return normalizeClientProductPairsDraftValue(value).length;
}

function formatClientProductPairSelectionSummary(selectedCount, totalCount) {
  const selectionLabel = `${selectedCount} association${selectedCount > 1 ? "s" : ""}`;
  if (totalCount > 0) {
    return `${selectionLabel} / ${totalCount}`;
  }
  return selectionLabel;
}

function partitionOptionsBySelection(options, selectedLookupKeys) {
  const selectedOptions = [];
  const unselectedOptions = [];

  options.forEach((option) => {
    const lookupKey = option.trim().toLocaleLowerCase("fr-CA");
    if (selectedLookupKeys.has(lookupKey)) {
      selectedOptions.push(option);
      return;
    }

    unselectedOptions.push(option);
  });

  return {
    orderedOptions: [...selectedOptions, ...unselectedOptions],
    firstUnselectedIndex: selectedOptions.length,
    showSelectionDivider: selectedOptions.length > 0 && unselectedOptions.length > 0,
  };
}

function ClientProductPairsSettingControl({ canEditSetting, onChange, setting, value }) {
  const selectedPairs = normalizeClientProductPairsDraftValue(value);
  const options = normalizeStringListSettingValue(setting?.options);
  const selectedCount = countSelectedClientProductPairs(selectedPairs);
  const completedCount = countCompletedClientProductPairs(selectedPairs);
  const selectedLookupKeys = new Set(
    selectedPairs.map((pair) => pair.client_name.trim().toLocaleLowerCase("fr-CA")),
  );
  const { orderedOptions, firstUnselectedIndex, showSelectionDivider } = partitionOptionsBySelection(options, selectedLookupKeys);

  if (options.length === 0) {
    return (
      <p className="m-0 rounded-[4px] border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-[0.76rem] leading-5 text-slate-500">
        Aucun client de chargement dans l'onglet « Clients ».
      </p>
    );
  }

  return (
    <div className="grid gap-1">
      <div className="flex flex-wrap items-center gap-1">
        <span className="inline-flex items-center rounded-[4px] border border-slate-200 bg-slate-50 px-1.75 py-0.4 text-[0.64rem] font-semibold text-slate-600">
          {formatClientProductPairSelectionSummary(selectedCount, options.length)}
        </span>
        <span className="text-[0.68rem] leading-3.5 text-slate-500">
          Le produit n’est injecté que si la colonne Excel Produit est vide.
        </span>
        {selectedCount > completedCount ? (
          <span className="text-[0.68rem] leading-3.5 text-slate-500">
            Les lignes cochées sans produit ne seront pas enregistrées.
          </span>
        ) : null}
      </div>

      <div className="grid h-40 gap-0.75 overflow-y-auto rounded-[4px] border border-slate-200 bg-white p-1">
        {orderedOptions.map((option, index) => {
          const selectedPair = resolveClientProductPair(selectedPairs, option);
          const isChecked = selectedPair !== null;

          return (
            <React.Fragment key={option}>
              {showSelectionDivider && index === firstUnselectedIndex ? (
                <div className="mx-1 my-px h-px rounded-[4px] bg-slate-200" />
              ) : null}

              <div
                className={joinClasses([
                  "grid min-w-0 gap-1 rounded-[4px] border px-2 py-0.75 transition min-[560px]:grid-cols-[minmax(0,1fr)_minmax(5.2rem,5.8rem)] min-[560px]:items-center",
                  isChecked
                    ? "border-slate-400 bg-slate-100"
                    : "border-slate-200 bg-slate-50/70 hover:border-slate-300 hover:bg-white",
                ])}
              >
                <label className={joinClasses([
                  "flex min-w-0 items-center gap-1.5 text-[0.77rem] font-medium",
                  isChecked ? "text-slate-900" : "text-slate-700",
                  !canEditSetting ? "cursor-default opacity-75" : "cursor-pointer",
                ])}>
                  <input
                    checked={isChecked}
                    className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-2 focus:ring-border-accent-subtle"
                    disabled={!canEditSetting}
                    onChange={(event) => onChange(setting.key, toggleClientProductPairSettingValue(selectedPairs, option, event.target.checked))}
                    type="checkbox"
                  />
                  <span className="min-w-0 flex-1 leading-4 whitespace-normal wrap-break-word">{option}</span>
                </label>

                <input
                  className="min-w-0 h-7 w-full rounded-[4px] border border-slate-200 bg-white px-2 py-0.5 text-[0.76rem] leading-4 font-medium text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 min-[560px]:justify-self-end"
                  disabled={!canEditSetting || !isChecked}
                  onChange={(event) => onChange(setting.key, updateClientProductPairSettingGrainType(selectedPairs, option, event.target.value))}
                  placeholder="Produit"
                  type="text"
                  value={selectedPair?.grain_type || ""}
                />
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function SollioCommentAmDefaultsCard({
  canEdit,
  groupMeta,
  onChange,
  settings,
  values,
}) {
  const enabledSetting = settings.find((setting) => setting.key === SOLLIO_COMMENT_AM_ENABLED_SETTING_KEY);
  const isEnabled = Boolean(values[SOLLIO_COMMENT_AM_ENABLED_SETTING_KEY] ?? enabledSetting?.value ?? true);
  const canToggle = enabledSetting && canEditSystemConfigSetting(enabledSetting, canEdit);

  return (
    <article className="grid gap-2.5 rounded-[4px] border border-black/6 bg-white px-3 py-2.5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      {groupMeta ? (
        <div className={SETTING_GROUP_HEADER_SURFACE_GRID_CLASS_NAME}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className={SETTING_GROUP_HEADER_TITLE_CLASS_NAME}>{groupMeta.title}</p>
            {enabledSetting ? (
              <button
                aria-label="Activer la règle Sollio AM"
                aria-pressed={isEnabled}
                className={joinClasses([
                  "rounded-[4px] border px-2.5 py-1 text-[0.8rem] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent-subtle disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none",
                  isEnabled
                    ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                    : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
                ])}
                disabled={!canToggle}
                onClick={() => onChange(SOLLIO_COMMENT_AM_ENABLED_SETTING_KEY, !isEnabled)}
                type="button"
              >
                {isEnabled ? "Règle AM activée" : "Règle AM désactivée"}
              </button>
            ) : null}
          </div>
          <p className="m-0 text-[0.8rem] leading-5 text-slate-600">
            {isEnabled ? groupMeta.description : "Désactivée : le commentaire “am” n’ajoute aucune heure ni priorité de déchargement."}
          </p>
        </div>
      ) : null}

      <div className="grid gap-2 min-[960px]:grid-cols-2">
        {settings.filter((setting) => setting.key !== SOLLIO_COMMENT_AM_ENABLED_SETTING_KEY).map((setting) => {
          const canEditSetting = isEnabled && canEditSystemConfigSetting(setting, canEdit);
          const isBooleanSetting = setting.value_type === "boolean";
          const isTimeOfDaySetting = setting.key === SOLLIO_COMMENT_AM_UNLOADING_START_HOUR_SETTING_KEY;
          const currentValue = values[setting.key] ?? resolveSettingDraftFallbackValue(setting);

          return (
            <div key={setting.key} className="grid min-w-0 gap-1.5 rounded-[4px] border border-slate-200 bg-slate-50 px-2.5 py-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <p className="m-0 text-[0.84rem] font-semibold tracking-[-0.02em] text-slate-950">{setting.label}</p>
                {setting.is_read_only ? (
                  <span className={joinClasses([
                    "inline-flex items-center rounded-[4px] px-1.75 py-0.5 text-[0.6rem] font-bold uppercase tracking-[0.12em]",
                    pillToneClasses.outline,
                  ])}>
                    {setting.availability_note || "Lecture seule"}
                  </span>
                ) : null}
              </div>

              <label className="grid min-w-0 gap-1">
                {isBooleanSetting ? (
                  <select
                    className="min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-2.5 py-1.75 text-[0.86rem] font-semibold text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                    disabled={!canEditSetting}
                    onChange={(event) => onChange(setting.key, event.target.value === "true")}
                    value={String(Boolean(currentValue))}
                  >
                    <option value="true">Activé</option>
                    <option value="false">Désactivé</option>
                  </select>
                ) : (
                  <div className="flex min-w-0 items-center gap-2">
                    <input
                      className="min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-2.5 py-1.75 text-[0.86rem] font-semibold text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                      disabled={!canEditSetting}
                      max={isTimeOfDaySetting ? formatHourInputBound(setting.max_value) : setting.max_value ?? undefined}
                      min={isTimeOfDaySetting ? formatHourInputBound(setting.min_value) : setting.min_value}
                      onChange={(event) => onChange(setting.key, event.target.value)}
                      step={isTimeOfDaySetting ? Math.round(Number(setting.step ?? 0) * 3600) || undefined : setting.step}
                      type={isTimeOfDaySetting ? "time" : "number"}
                      value={currentValue}
                    />
                    {setting.unit && !isTimeOfDaySetting ? (
                      <span className="inline-flex shrink-0 items-center rounded-[4px] border border-slate-200 bg-white px-2 py-0.6 text-[0.64rem] font-semibold text-slate-600">
                        {setting.unit}
                      </span>
                    ) : null}
                  </div>
                )}
              </label>

              {setting.is_read_only && setting.availability_note ? (
                <p className="m-0 text-[0.68rem] font-medium leading-4 text-slate-500">{setting.availability_note}</p>
              ) : null}
            </div>
          );
        })}
      </div>
    </article>
  );
}

function buildDisplaySettingGroups(sectionKey, settings) {
  if (sectionKey === "order_import_defaults") {
    const displayGroups = [];
    let mergedSollioCommentAmGroup = false;

    settings.forEach((setting) => {
      const groupKey = typeof setting?.group === "string" && setting.group.trim()
        ? setting.group.trim()
        : "default";

      if (groupKey === "sollio_comment_am_defaults") {
        if (mergedSollioCommentAmGroup) {
          return;
        }

        mergedSollioCommentAmGroup = true;
        displayGroups.push([
          groupKey,
          settings.filter((candidate) => candidate?.group === "sollio_comment_am_defaults"),
        ]);
        return;
      }

      displayGroups.push([`${groupKey}:${setting.key}`, [setting]]);
    });

    return displayGroups;
  }

  if (sectionKey !== "solver_settings") {
    return [["default", settings]];
  }

  const groupedSettings = groupSettingsByGroup(settings);
  const displayGroups = [];
  let mergedPreAnchorGroup = false;
  let mergedTrafficRoutingGroup = false;

  Object.entries(groupedSettings).forEach(([groupKey, groupSettings]) => {
    if (PRE_ANCHOR_GROUP_KEYS.has(groupKey)) {
      if (mergedPreAnchorGroup) {
        return;
      }

      mergedPreAnchorGroup = true;
      const mergedSettings = [
        ...(Array.isArray(groupedSettings.pre_anchor_bonus) ? groupedSettings.pre_anchor_bonus : []),
        ...(Array.isArray(groupedSettings.pre_anchor_scope) ? groupedSettings.pre_anchor_scope : []),
      ].sort((left, right) => {
        const leftRank = PRE_ANCHOR_SETTING_ORDER[left?.key] || 99;
        const rightRank = PRE_ANCHOR_SETTING_ORDER[right?.key] || 99;
        return leftRank - rightRank;
      });
      displayGroups.push([PRE_ANCHOR_BUNDLE_GROUP_KEY, mergedSettings]);
      return;
    }

    if (TRAFFIC_ROUTING_GROUP_KEYS.has(groupKey)) {
      if (mergedTrafficRoutingGroup) {
        return;
      }

      mergedTrafficRoutingGroup = true;
      displayGroups.push([
        TRAFFIC_ROUTING_BUNDLE_GROUP_KEY,
        [
          ...(Array.isArray(groupedSettings.here_routing) ? groupedSettings.here_routing : []),
          ...(Array.isArray(groupedSettings.traffic_routing) ? groupedSettings.traffic_routing : []),
        ],
      ]);
      return;
    }

    displayGroups.push([groupKey, groupSettings]);
  });

  return displayGroups;
}

function HoverCardInfoButton({ ariaLabel, content, title }) {
  return (
    <HoverCard content={content} delay={180} maxWidth={420} placement="top" title={title}>
      <button
        aria-label={ariaLabel || title}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[4px] border border-slate-200 bg-white text-slate-500 transition-colors duration-150 hover:border-slate-300 hover:text-slate-900"
        type="button"
      >
        <CircleHelp aria-hidden="true" size={14} />
      </button>
    </HoverCard>
  );
}

function PreAnchorBundleHoverCardContent() {
  return (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <p className="m-0 text-[0.68rem] font-black uppercase tracking-[0.16em] text-slate-500">Décision d’assignation</p>
        <p className="m-0 text-sm leading-6 text-slate-600">
          Ces réglages sont lus ensemble quand le solveur compare plusieurs camions pour une commande ancre P1, P2 ou P3.
        </p>
      </div>

      <div className="grid gap-1.5 rounded-[4px] border border-black/6 bg-slate-50/85 px-3 py-2.5">
        <p className="m-0 text-[0.68rem] font-black uppercase tracking-[0.16em] text-slate-500">Base du bonus</p>
        <p className="m-0 text-[0.82rem] leading-5 text-slate-600">
          Le calcul valorise les minutes absorbées avant l’ancre par la route et par les opérations de chargement ou de déchargement déjà placées dans la séquence avant cette ancre.
        </p>
      </div>

      <div className="grid gap-1.5 rounded-[4px] border border-black/6 bg-slate-50/85 px-3 py-2.5">
        <p className="m-0 text-[0.68rem] font-black uppercase tracking-[0.16em] text-slate-500">Effet sur le score</p>
        <p className="m-0 text-[0.82rem] leading-5 text-slate-600">
          Le solveur retranche ce bonus du coût projeté du camion. Plus le CAD/min est élevé sur P1, P2 ou P3, plus ce remplissage utile peut orienter la sélection du camion retenu.
        </p>
      </div>

      <p className="m-0 text-[0.77rem] leading-5 text-slate-500">
        Portée actuelle: chargement et déchargement sont tous deux disponibles. Les deux interrupteurs restent indépendants pour laisser le choix par famille d’ancre.
      </p>
    </div>
  );
}

function SameOdGroupingHoverCardContent() {
  return (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <p className="m-0 text-[0.68rem] font-black uppercase tracking-[0.16em] text-slate-500">Ce que le rayon contrôle</p>
        <p className="m-0 text-sm leading-6 text-slate-600">
          Ce rayon permet seulement d’essayer un regroupement après le calcul. Le regroupement doit encore respecter la capacité, les heures du camion et les coûts.
        </p>
      </div>

      <div className="grid gap-1.5 rounded-[4px] border border-black/6 bg-slate-50/85 px-3 py-2.5">
        <p className="m-0 text-[0.68rem] font-black uppercase tracking-[0.16em] text-slate-500">Avant</p>
        <p className="m-0 text-[0.82rem] leading-5 text-slate-600">
          Le post-traitement regroupait surtout les commandes avec même chargement et même destination exacte. Deux fermes proches mais à adresses différentes pouvaient être rejetées comme destination différente.
        </p>
      </div>

      <div className="grid gap-1.5 rounded-[4px] border border-black/6 bg-slate-50/85 px-3 py-2.5">
        <p className="m-0 text-[0.68rem] font-black uppercase tracking-[0.16em] text-slate-500">Aujourd’hui</p>
        <p className="m-0 text-[0.82rem] leading-5 text-slate-600">
          Si deux livraisons partagent le même chargement et le même produit, le solveur peut tester un regroupement quand leur distance Haversine est sous ce rayon. 0 km désactive ce test; 6 à 10 km vise un voisinage réel; 50 km ouvre des secteurs beaucoup plus larges.
        </p>
      </div>

      <p className="m-0 text-[0.77rem] leading-5 text-slate-500">
        Exemple: deux fermes voisines sont assez proches pour être candidates avec 6 km. Passer à 50 km n’aide pas plus ce cas précis; cela ajoute surtout d’autres candidats potentiels plus éloignés.
      </p>
    </div>
  );
}

function SettingsFieldCard({
  canEdit,
  onChange,
  setting,
  value,
}) {
  const canEditSetting = canEditSystemConfigSetting(setting, canEdit);
  const isBooleanSetting = setting.value_type === "boolean";
  const isTimeSetting = setting.value_type === "time";
  const isClientProductPairsSetting = setting.value_type === "client_product_pairs";
  const isCompactImportDefaultSetting = setting.section === "order_import_defaults"
    && isClientProductPairsSetting;
  const isFullWidthControlSetting = isCompactImportDefaultSetting;
  const currentValue = isClientProductPairsSetting
      ? normalizeClientProductPairsDraftValue(value)
      : isTimeSetting
        ? toTimeSettingInputValue(value)
      : value;
  const renderNearDeliveryProbeHelp = setting.key === "near_delivery_probe_max_distance_km";

  return (
    <article className={joinClasses([
      "grid min-w-0 rounded-[4px] border border-black/6 bg-white px-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]",
      isCompactImportDefaultSetting ? "gap-1.5 py-2" : "gap-2 py-2.5",
    ])}>
      <div className={isCompactImportDefaultSetting ? SETTING_GROUP_HEADER_SURFACE_GRID_CLASS_NAME : "contents"}>
        <div className={joinClasses([
          "flex flex-wrap items-start justify-between",
          isCompactImportDefaultSetting ? "gap-1.5" : "gap-2",
        ])}>
          <div className={joinClasses([
            "grid min-w-0 flex-1",
            isCompactImportDefaultSetting ? "gap-0.5" : "gap-1",
          ])}>
            <div className="flex flex-wrap items-center gap-2">
              <p className="m-0 text-[0.88rem] font-semibold tracking-[-0.02em] text-slate-950">{setting.label}</p>
              {setting.is_read_only ? (
                <span className={joinClasses([
                  "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.12em]",
                  pillToneClasses.outline,
                ])}>
                  {setting.availability_note || "Lecture seule"}
                </span>
              ) : null}
            </div>
            {setting.is_read_only && setting.availability_note ? (
              <p className="m-0 text-[0.72rem] font-medium text-slate-500">{setting.availability_note}</p>
            ) : null}
          </div>
          {renderNearDeliveryProbeHelp ? (
            <HoverCardInfoButton
              ariaLabel="Expliquer le rayon de regroupement des livraisons proches"
              content={<SameOdGroupingHoverCardContent />}
              title="Regroupement des livraisons proches"
            />
          ) : null}
        </div>

        <p className={joinClasses([
          "m-0 text-slate-600",
          isCompactImportDefaultSetting ? "text-[0.75rem] leading-[1.02rem]" : "text-[0.78rem] leading-[1.1rem]",
        ])}>{setting.description}</p>
      </div>

      <div className={joinClasses([
        "grid min-w-0 min-[720px]:items-start",
        isFullWidthControlSetting
          ? "gap-1"
          : "min-[720px]:grid-cols-[max-content_minmax(0,1fr)] gap-1.5 min-[720px]:gap-2",
      ])}>
          {!isFullWidthControlSetting ? (
            <span className={joinClasses([
              SETTING_VALUE_ACTIVE_LABEL_CLASS_NAME,
              "pt-1",
            ])}>Valeur active</span>
          ) : null}
          {isClientProductPairsSetting ? (
            <ClientProductPairsSettingControl canEditSetting={canEditSetting} onChange={onChange} setting={setting} value={currentValue} />
          ) : isBooleanSetting ? (
            <select
              className="min-w-[9rem] w-full max-w-48 justify-self-start rounded-[4px] border border-slate-200 bg-white px-3 py-2 text-[0.92rem] font-semibold text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
              disabled={!canEditSetting}
              onChange={(event) => onChange(setting.key, event.target.value === "true")}
              value={String(Boolean(currentValue))}
            >
              <option value="true">Activé</option>
              <option value="false">Désactivé</option>
            </select>
          ) : isTimeSetting ? (
            <input
              className="min-w-0 w-full max-w-48 justify-self-start rounded-[4px] border border-slate-200 bg-white px-3 py-2 text-[0.92rem] font-semibold text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
              disabled={!canEditSetting}
              onChange={(event) => onChange(setting.key, event.target.value)}
              type="time"
              value={currentValue}
            />
          ) : (
            <div className="flex min-w-0 items-center gap-2">
              <input
                className="min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-3 py-2 text-[0.92rem] font-semibold text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                disabled={!canEditSetting}
                max={setting.max_value ?? undefined}
                min={setting.min_value}
                onChange={(event) => onChange(setting.key, event.target.value)}
                step={setting.step}
                type="number"
                value={currentValue}
              />
              {setting.unit ? (
                <span className="inline-flex shrink-0 items-center rounded-[4px] border border-slate-200 bg-slate-50 px-2.5 py-0.75 text-[0.7rem] font-semibold text-slate-600">
                  {setting.unit}
                </span>
              ) : null}
            </div>
          )}
      </div>
    </article>
  );
}

function GroupedSettingsFieldCard({
  canEdit,
  compactGridClassNameOverride = null,
  headerAccessory = null,
  groupMeta,
  onChange,
  settings,
  values,
}) {
  const compactGridClassName = compactGridClassNameOverride || (
    settings.length >= 5
      ? "min-[720px]:grid-cols-2 min-[1040px]:grid-cols-3 min-[1500px]:grid-cols-5"
      : settings.length >= 3
        ? "min-[1120px]:grid-cols-3"
        : "min-[920px]:grid-cols-2"
  );

  return (
    <article className="grid gap-2.5 rounded-[4px] border border-black/6 bg-white px-3 py-2.5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      {groupMeta ? (
        <div className={SETTING_GROUP_HEADER_SURFACE_FLEX_CLASS_NAME}>
          <div className="grid gap-0.5">
            <p className={SETTING_GROUP_HEADER_TITLE_COMPACT_CLASS_NAME}>{groupMeta.title}</p>
            <p className="m-0 text-[0.77rem] leading-5 text-slate-600">{groupMeta.description}</p>
          </div>
          {headerAccessory}
        </div>
      ) : null}

      <div className={joinClasses(["grid gap-2.5", compactGridClassName])}>
        {settings.map((setting) => {
          const canEditSetting = canEditSystemConfigSetting(setting, canEdit);
          const isBooleanSetting = setting.value_type === "boolean";
          const isTimeSetting = setting.value_type === "time";
          const currentValue = isTimeSetting
            ? toTimeSettingInputValue(values[setting.key] ?? resolveSettingDraftFallbackValue(setting))
            : values[setting.key] ?? resolveSettingDraftFallbackValue(setting);
          const renderNearDeliveryProbeHelp = setting.key === "near_delivery_probe_max_distance_km";

          return (
            <div key={setting.key} className="grid min-w-0 gap-1.5 rounded-[4px] border border-slate-200 bg-slate-50 px-2.5 py-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="m-0 text-[0.82rem] font-semibold tracking-[-0.02em] text-slate-950">{resolveGroupedSettingLabel(setting)}</p>
                  {setting.is_read_only ? (
                    <span className={joinClasses([
                      "inline-flex items-center rounded-[4px] px-1.75 py-0.5 text-[0.6rem] font-bold uppercase tracking-[0.12em]",
                      pillToneClasses.outline,
                    ])}>
                      {setting.availability_note || "Lecture seule"}
                    </span>
                  ) : null}
                </div>
                {renderNearDeliveryProbeHelp ? (
                  <HoverCardInfoButton
                    ariaLabel="Expliquer le rayon de regroupement des livraisons proches"
                    content={<SameOdGroupingHoverCardContent />}
                    title="Regroupement des livraisons proches"
                  />
                ) : null}
              </div>

              <label className="grid min-w-0 gap-1.5">
                <span className={SETTING_VALUE_ACTIVE_LABEL_COMPACT_CLASS_NAME}>Valeur active</span>
                {isBooleanSetting ? (
                  <select
                    className="min-w-[8rem] w-full max-w-44 justify-self-start rounded-[4px] border border-slate-200 bg-white px-2.5 py-1.75 text-[0.86rem] font-semibold text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                    disabled={!canEditSetting}
                    onChange={(event) => onChange(setting.key, event.target.value === "true")}
                    value={String(Boolean(currentValue))}
                  >
                    <option value="true">Activé</option>
                    <option value="false">Désactivé</option>
                  </select>
                ) : isTimeSetting ? (
                  <input
                    className="min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-2.5 py-1.75 text-[0.86rem] font-semibold text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                    disabled={!canEditSetting}
                    onChange={(event) => onChange(setting.key, event.target.value)}
                    type="time"
                    value={currentValue}
                  />
                ) : (
                  <div className="flex min-w-0 items-center gap-2">
                    <input
                      className="min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-2.5 py-1.75 text-[0.86rem] font-semibold text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                      disabled={!canEditSetting}
                      max={setting.max_value ?? undefined}
                      min={setting.min_value}
                      onChange={(event) => onChange(setting.key, event.target.value)}
                      step={setting.step}
                      type="number"
                      value={currentValue}
                    />
                    {setting.unit ? (
                      <span className="inline-flex shrink-0 items-center rounded-[4px] border border-slate-200 bg-white px-2 py-0.6 text-[0.64rem] font-semibold text-slate-600">
                        {setting.unit}
                      </span>
                    ) : null}
                  </div>
                )}
              </label>

              {setting.is_read_only && setting.availability_note ? (
                <p className="m-0 text-[0.68rem] font-medium leading-4 text-slate-500">{setting.availability_note}</p>
              ) : null}
            </div>
          );
        })}
      </div>
    </article>
  );
}

function SettingSectionCard({
  canEdit,
  footerContent = null,
  hiddenGroupKeys = [],
  onChange,
  sectionKey,
  settings,
  values,
}) {
  const sectionMeta = SECTION_META[sectionKey] || {
    badge: "Moteur",
    title: sectionKey,
    description: "Réglages disponibles pour cette section.",
  };
  const sectionGroups = buildDisplaySettingGroups(sectionKey, settings);
  const hiddenGroupKeySet = hiddenGroupKeys.length ? new Set(hiddenGroupKeys) : null;
  const visibleSectionGroups = hiddenGroupKeySet
    ? sectionGroups.filter(([groupKey]) => !hiddenGroupKeySet.has(groupKey))
    : sectionGroups;
  const compactSolverGroups = sectionKey === "solver_settings"
    ? visibleSectionGroups.filter(([groupKey]) => SOLVER_COMPACT_GROUP_KEYS.has(groupKey))
    : [];
  const standardVisibleGroups = sectionKey === "solver_settings"
    ? visibleSectionGroups.filter(([groupKey]) => !SOLVER_COMPACT_GROUP_KEYS.has(groupKey))
    : visibleSectionGroups;

  const renderSectionGroup = ([groupKey, groupSettings]) => {
    const groupMeta = SETTING_GROUP_META[groupKey];
    const renderTrafficRoutingGroupedCard = groupKey === TRAFFIC_ROUTING_BUNDLE_GROUP_KEY;
    const renderGroupedCard = groupKey === PRE_ANCHOR_BUNDLE_GROUP_KEY || renderTrafficRoutingGroupedCard;
    const renderSollioCommentAmCard = groupKey === "sollio_comment_am_defaults";
    const renderTruckWorkTimeSubgroups = groupKey === "truck_work_time";
    const renderSetting = (setting) => (
      <SettingsFieldCard
        canEdit={canEdit}
        key={setting.key}
        onChange={onChange}
        setting={setting}
        value={values[setting.key] ?? resolveSettingDraftFallbackValue(setting)}
      />
    );

    return (
      <div key={`${sectionKey}-${groupKey}`} className="grid gap-2.5">
        {renderGroupedCard ? (
          <GroupedSettingsFieldCard
            canEdit={canEdit}
            compactGridClassNameOverride={renderTrafficRoutingGroupedCard ? "min-[1120px]:grid-cols-3" : null}
            headerAccessory={groupKey === PRE_ANCHOR_BUNDLE_GROUP_KEY ? (
              <HoverCardInfoButton
                ariaLabel="Expliquer le rôle du bonus pré-ancre dans l’assignation"
                content={<PreAnchorBundleHoverCardContent />}
                title="Bonus pré-ancre et portée"
              />
            ) : null}
            groupMeta={groupMeta}
            onChange={onChange}
            settings={groupSettings}
            values={values}
          />
        ) : renderSollioCommentAmCard ? (
          <SollioCommentAmDefaultsCard
            canEdit={canEdit}
            groupMeta={groupMeta}
            onChange={onChange}
            settings={groupSettings}
            values={values}
          />
        ) : (
          <>
            {groupMeta ? (
              <div className={SETTING_GROUP_HEADER_SURFACE_GRID_CLASS_NAME}>
                <p className={SETTING_GROUP_HEADER_TITLE_CLASS_NAME}>{groupMeta.title}</p>
                <p className="m-0 text-[0.8rem] leading-5 text-slate-600">{groupMeta.description}</p>
              </div>
            ) : null}

            {renderTruckWorkTimeSubgroups ? (
              [
                ...TRUCK_WORK_TIME_SUBGROUPS,
                {
                  title: "Autres réglages",
                  keys: groupSettings
                    .map((setting) => setting.key)
                    .filter((key) => !TRUCK_WORK_TIME_SUBGROUPS.some((subgroup) => subgroup.keys.includes(key))),
                },
              ].map((subgroup) => {
                const subgroupSettings = subgroup.keys
                  .map((key) => groupSettings.find((setting) => setting.key === key))
                  .filter(Boolean);
                return subgroupSettings.length ? (
                  <div key={subgroup.title} className="grid gap-1.5">
                    <p className="m-0 border-b border-slate-200 pb-1 text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-slate-700">{subgroup.title}</p>
                    <div className={joinClasses([
                      "grid gap-2.5 min-[720px]:grid-cols-2",
                      subgroupSettings.length >= 4 ? "min-[1240px]:grid-cols-4" : "min-[1040px]:grid-cols-3",
                    ])}>
                      {subgroupSettings.map(renderSetting)}
                    </div>
                  </div>
                ) : null;
              })
            ) : (
            <div className={joinClasses([
              "grid gap-2.5",
              groupSettings.length > 1
                ? groupKey === ROUTE_CONTINUITY_GROUP_KEY || groupKey === "same_od_pair_grouping"
                    ? "min-[1040px]:grid-cols-3"
                    : sectionKey !== "order_import_defaults"
                      ? "min-[1040px]:grid-cols-2"
                      : ""
                : "",
            ])}>
              {groupSettings.map(renderSetting)}
            </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div
      className={joinClasses([
        "grid gap-2.5 rounded-[4px] border border-black/6 bg-white/88 px-3 py-2.5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]",
        SYSTEM_CONFIG_SECTION_SCROLL_MARGIN_CLASS_NAME,
      ])}
      id={resolveSystemConfigSectionId(sectionKey)}
    >
      <div className="flex flex-wrap items-start justify-between gap-2.5">
        <div className="grid gap-1">
          <span className={joinClasses([SYSTEM_CONFIG_SECTION_TAG_CLASS_NAME, "px-2.5 py-1 text-[0.64rem]"])}>{sectionMeta.badge}</span>
          {sectionMeta.title ? (
            <p className="m-0 text-[1rem] font-semibold tracking-[-0.025em] text-slate-950">{sectionMeta.title}</p>
          ) : null}
          {sectionMeta.description ? (
            <p className="m-0 text-[0.8rem] leading-5 text-slate-600">{sectionMeta.description}</p>
          ) : null}
        </div>
      </div>

      <div className={joinClasses([
        "grid gap-2.5",
        sectionKey === "order_import_defaults" ? "min-[1040px]:grid-cols-2 min-[1040px]:items-start" : "",
      ])}>
        {compactSolverGroups.length ? (
          <div className="grid gap-2.5 min-[1120px]:grid-cols-3 min-[1120px]:items-start">
            {compactSolverGroups.map(renderSectionGroup)}
          </div>
        ) : null}
        {standardVisibleGroups.map(renderSectionGroup)}

        {footerContent}
      </div>
    </div>
  );
}

function ToleranceTableCard({
  canEdit,
  draftValues,
  onChange,
  tolerances,
}) {
  if (!Array.isArray(tolerances) || tolerances.length === 0) {
    return null;
  }

  return (
    <article className="grid rounded-[4px] border border-black/6 bg-white/88 px-2 py-1.5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="overflow-hidden rounded-[4px] border border-black/6 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
        <div className="min-w-0 overflow-x-hidden">
          <table className="min-w-0 w-full table-fixed border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="w-18 min-[960px]:w-20 border-b border-slate-200 bg-slate-50/80 px-2 py-1.25 text-left text-[0.64rem] font-semibold uppercase tracking-[0.12em] text-slate-500" scope="col">
                  Priorité
                </th>
                <th className="w-24 min-[960px]:w-26 border-b border-slate-200 bg-slate-50/80 px-2 py-1.25 text-left text-[0.64rem] font-semibold uppercase tracking-[0.12em] text-slate-500" scope="col">
                  Active
                </th>
                <th className="w-20 min-[960px]:w-22 border-b border-slate-200 bg-slate-50/80 px-2 py-1.25 text-left text-[0.64rem] font-semibold uppercase tracking-[0.12em] text-slate-500" scope="col">
                  Avant
                </th>
                <th className="w-20 min-[960px]:w-22 border-b border-slate-200 bg-slate-50/80 px-2 py-1.25 text-left text-[0.64rem] font-semibold uppercase tracking-[0.12em] text-slate-500" scope="col">
                  Après
                </th>
              </tr>
            </thead>
            <tbody>
              {(tolerances || []).map((tolerance, index, list) => {
                const canEditTolerance = canEditSystemConfigTolerance(tolerance, canEdit);
                const draftValue = draftValues[tolerance.priority] || { before: "", after: "" };
                const isSymmetric = tolerance.priority !== "P1";
                const rowClassName = index === list.length - 1 ? "border-b-0" : "border-b";

                return (
                  <tr key={tolerance.priority} className="align-middle">
                    <td className={joinClasses(["px-2 py-1.25 align-middle border-slate-200", rowClassName])}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center rounded-[4px] border border-slate-200 bg-slate-50 px-1.5 py-0.35 text-[0.7rem] font-semibold text-slate-700">
                          {tolerance.priority}
                        </span>
                      </div>
                    </td>
                    <td className={joinClasses(["px-2 py-1.25 align-middle border-slate-200", rowClassName])}>
                      <span className="inline-flex items-center rounded-[4px] border border-slate-200 bg-slate-50 px-1.75 py-0.5 text-[0.7rem] font-semibold text-slate-700">
                        {tolerance.tolerance_before_minutes} / {tolerance.tolerance_after_minutes} min
                      </span>
                    </td>
                    <td className={joinClasses(["px-2 py-1.25 align-middle border-slate-200", rowClassName])}>
                      <input
                        aria-label={`Tolérance ${tolerance.priority} avant l’horaire`}
                        className="min-w-0 h-9 w-full rounded-[4px] border border-slate-200 bg-white px-2 py-1 text-[0.8rem] font-semibold text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                        disabled={!canEditTolerance}
                        min={0}
                        onChange={(event) => onChange(tolerance.priority, "before", event.target.value)}
                        step={1}
                        type="number"
                        value={draftValue.before}
                      />
                    </td>
                    <td className={joinClasses(["px-2 py-1.25 align-middle border-slate-200", rowClassName])}>
                      <input
                        aria-label={`Tolérance ${tolerance.priority} après l’horaire`}
                        className="min-w-0 h-9 w-full rounded-[4px] border border-slate-200 bg-white px-2 py-1 text-[0.8rem] font-semibold text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                        disabled={!canEditTolerance || isSymmetric}
                        min={0}
                        onChange={(event) => onChange(tolerance.priority, "after", event.target.value)}
                        step={1}
                        type="number"
                        value={draftValue.after}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </article>
  );
}

function StrictP2P3TimeWindowsSettingCard({
  canEdit,
  onChange,
  setting,
  value,
}) {
  const switchId = React.useId();
  if (!setting) {
    return null;
  }

  const canEditSetting = canEditSystemConfigSetting(setting, canEdit);
  const isEnabled = value === true;
  const labelId = switchId + "-label";
  const descriptionId = switchId + "-description";

  return (
    <article
      className="flex flex-wrap items-center justify-between gap-3 rounded-[4px] border border-black/6 bg-white px-3 py-2.5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]"
      data-system-config-strict-p2-p3-time-windows="true"
    >
      <div className="grid min-w-0 flex-1 gap-0.5">
        <p id={labelId} className="m-0 text-[0.84rem] font-semibold tracking-[-0.02em] text-slate-950">P2/P3 strictes</p>
        <p id={descriptionId} className="m-0 text-[0.76rem] leading-5 text-slate-600">Chaque priorité conserve sa propre tolérance. Hors tolérance, la commande devient non assignable.</p>
      </div>

      <label
        className={joinClasses([
          "relative inline-flex shrink-0 items-center gap-2",
          canEditSetting ? "cursor-pointer" : "cursor-not-allowed opacity-60",
        ])}
        htmlFor={switchId}
      >
        <input
          id={switchId}
          aria-checked={isEnabled}
          aria-describedby={descriptionId}
          aria-labelledby={labelId}
          checked={isEnabled}
          className="sr-only"
          disabled={!canEditSetting}
          onChange={(event) => onChange(setting.key, event.target.checked)}
          role="switch"
          type="checkbox"
        />
        <span
          aria-hidden="true"
          className={joinClasses([
            "inline-flex h-6 w-11 items-center rounded-full border p-0.5 transition-colors",
            isEnabled
              ? "border-legacy-teal bg-legacy-teal"
              : "border-slate-300 bg-slate-200",
          ])}
        >
          <span className={joinClasses([
            "h-4.5 w-4.5 rounded-full bg-white shadow-sm transition-transform",
            isEnabled ? "translate-x-5" : "translate-x-0",
          ])} />
        </span>
        <span className="text-[0.7rem] font-semibold text-slate-700">
          {isEnabled ? "Activé" : "Désactivé"}
        </span>
      </label>
    </article>
  );
}

function OptimizationAnimationPreferenceCard({
  disabled = false,
  enabled = true,
  isSaving = false,
  onChange,
}) {
  const switchId = React.useId();
  const labelId = switchId + "-label";
  const descriptionId = switchId + "-description";

  return (
    <section
      className="grid gap-2.5 rounded-[4px] border border-black/6 bg-[linear-gradient(180deg,rgba(240,253,250,0.72),rgba(255,255,255,0.98))] px-3 py-3 shadow-[0_12px_28px_rgba(15,23,42,0.045)] max-[720px]:px-3"
      data-system-config-user-display-preferences="true"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-[4px] border border-black/6 bg-white px-3 py-2.5">
        <div className="grid min-w-0 flex-1 gap-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <p className="m-0 text-[0.92rem] font-semibold tracking-[-0.02em] text-slate-950">Préférences d’affichage</p>
            <span className={joinClasses([SYSTEM_CONFIG_SECTION_TAG_CLASS_NAME, "px-2 py-0.5 text-[0.6rem]"])}>Personnel</span>
          </div>
        </div>

        <span className="text-[0.68rem] font-semibold text-slate-500">
          {isSaving ? "Enregistrement…" : "Enregistré automatiquement"}
        </span>
      </div>

      <article className="flex flex-wrap items-center justify-between gap-3 rounded-[4px] border border-black/6 bg-white px-3 py-2.5 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
        <div className="grid min-w-0 flex-1 gap-0.5">
          <p id={labelId} className="m-0 text-[0.84rem] font-semibold tracking-[-0.02em] text-slate-950">Animation de l’optimisation</p>
          <p id={descriptionId} className="m-0 text-[0.76rem] leading-5 text-slate-600">Affiche les mouvements provisoires sur le Gantt. Le calcul et le résultat final restent identiques.</p>
        </div>

        <label
          className={joinClasses([
            "relative inline-flex shrink-0 items-center gap-2",
            disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          ])}
          htmlFor={switchId}
        >
          <input
            id={switchId}
            aria-checked={enabled}
            aria-describedby={descriptionId}
            aria-labelledby={labelId}
            checked={enabled}
            className="sr-only"
            data-optimization-animation-toggle="true"
            disabled={disabled}
            onChange={(event) => onChange?.(event.target.checked)}
            role="switch"
            type="checkbox"
          />
          <span
            aria-hidden="true"
            className={joinClasses([
              "inline-flex h-6 w-11 items-center rounded-full border p-0.5 transition-colors",
              enabled
                ? "border-legacy-teal bg-legacy-teal"
                : "border-slate-300 bg-slate-200",
            ])}
          >
            <span className={joinClasses([
              "h-4.5 w-4.5 rounded-full bg-white shadow-sm transition-transform",
              enabled ? "translate-x-5" : "translate-x-0",
            ])} />
          </span>
          <span className="text-[0.7rem] font-semibold text-slate-700">
            {enabled ? "Activée" : "Désactivée"}
          </span>
        </label>
      </article>
    </section>
  );
}

function PrioritySettingsSection({
  canEdit,
  draftToleranceValues,
  onSettingChange,
  onToleranceChange,
  prioritySettings,
  settingValues,
  strictP2P3TimeWindowsSetting,
  tolerances,
}) {
  const hasPrioritySettings = Array.isArray(prioritySettings) && prioritySettings.length > 0;
  const hasStrictP2P3TimeWindowsSetting = Boolean(strictP2P3TimeWindowsSetting);
  const hasTolerances = Array.isArray(tolerances) && tolerances.length > 0;

  if (!hasPrioritySettings && !hasStrictP2P3TimeWindowsSetting && !hasTolerances) {
    return null;
  }

  return (
    <section
      className={joinClasses([
        "grid gap-2.5 rounded-[4px] border border-black/6 bg-[linear-gradient(180deg,rgba(248,250,252,0.96),rgba(255,255,255,0.98))] px-3 py-3 shadow-[0_12px_28px_rgba(15,23,42,0.045)] max-[720px]:px-3",
        SYSTEM_CONFIG_SECTION_SCROLL_MARGIN_CLASS_NAME,
      ])}
      id={resolveSystemConfigSectionId("priorities")}
    >
      <div className="grid gap-1">
        <span className={joinClasses([SYSTEM_CONFIG_SECTION_TAG_CLASS_NAME, "px-2.5 py-1 text-[0.64rem]"])}>Priorités</span>
        <p className="m-0 text-[1rem] font-semibold tracking-[-0.025em] text-slate-950">Paramètres liés aux priorités</p>
        <p className="m-0 text-[0.8rem] leading-5 text-slate-600">Le bonus pré-ancre et les fenêtres temporelles P1, P2 et P3 sont regroupés ici. Le bonus de regroupement même chargement-livraison reste séparé.</p>
      </div>

      <div className="grid gap-2.5 min-[1360px]:grid-cols-[minmax(0,0.86fr)_minmax(0,1.14fr)] min-[1360px]:items-start">
        <div className="grid gap-2.5">
          {hasStrictP2P3TimeWindowsSetting ? (
            <StrictP2P3TimeWindowsSettingCard
              canEdit={canEdit}
              onChange={onSettingChange}
              setting={strictP2P3TimeWindowsSetting}
              value={settingValues[strictP2P3TimeWindowsSetting.key] ?? false}
            />
          ) : null}

          {hasTolerances ? (
            <ToleranceTableCard
              canEdit={canEdit}
              draftValues={draftToleranceValues}
              onChange={onToleranceChange}
              tolerances={tolerances}
            />
          ) : null}
        </div>

        {hasPrioritySettings ? (
          <GroupedSettingsFieldCard
            canEdit={canEdit}
            headerAccessory={(
              <HoverCardInfoButton
                ariaLabel="Expliquer le rôle du bonus pré-ancre dans l’assignation"
                content={<PreAnchorBundleHoverCardContent />}
                title="Bonus pré-ancre et portée"
              />
            )}
            groupMeta={SETTING_GROUP_META[PRE_ANCHOR_BUNDLE_GROUP_KEY]}
            onChange={onSettingChange}
            settings={prioritySettings}
            values={settingValues}
          />
        ) : null}
      </div>
    </section>
  );
}

function SystemConfigPageFrame({ children, isPending = false }) {
  return (
    <div className={joinClasses([
      "system-config-page mx-auto grid w-full min-w-0 px-4 pt-0",
      isPending ? "gap-4 pb-6" : "gap-3 pb-5",
      SYSTEM_CONFIG_SURFACE_WIDTH_CLASS_NAME,
    ])}>
      <WorkspacePageHeader
        className="system-config-hero"
        icon={SlidersHorizontal}
        title="Paramètres système"
        titleAs="h1"
        description={<span className="system-config-hero-subtitle">Ajuster les paramètres globaux du moteur et les tolérances métier.</span>}
      />
      {children}
    </div>
  );
}

export default function SystemConfigPage() {
  const { access } = useAccess();
  const { showToast, updateToast } = useToast();
  const {
    isSaving: isUserUiPreferenceSaving,
    optimizationAnimationEnabled,
    setOptimizationAnimationEnabled,
    userId: preferencesUserId,
  } = useUserUiPreferences();
  const [state, setState] = React.useState(null);
  const [draft, setDraft] = React.useState(() => buildDraftFromState(null));
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isHistoryCollapsed, setIsHistoryCollapsed] = React.useState(true);
  const [feedback, setFeedback] = React.useState(null);
  const [loadError, setLoadError] = React.useState("");
  const loadState = React.useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) {
      setIsLoading(true);
    }
    setLoadError("");
    try {
      const nextState = await fetchSystemConfigState();
      setState(nextState);
      setDraft(buildDraftFromState(nextState));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Impossible de charger les paramètres système.");
    } finally {
      if (!quiet) {
        setIsLoading(false);
      }
    }
  }, []);

  React.useEffect(() => {
    void loadState();
  }, [loadState]);

  const updatePayload = React.useMemo(() => buildUpdatePayload(state, draft), [draft, state]);
  const hasChanges = updatePayload.settings.length > 0 || updatePayload.time_window_tolerances.length > 0;
  const runtimeAffectingSettingsChanged = React.useMemo(() => {
    return updatePayload.settings.some((setting) => RUNTIME_AFFECTING_SYSTEM_SETTING_KEYS.has(setting?.key));
  }, [updatePayload.settings]);
  const dispatchPeriodToggleChanged = React.useMemo(() => (
    updatePayload.settings.some(
      (setting) => setting?.key === DISPATCH_PERIOD_TOGGLE_ENABLED_SETTING_KEY,
    )
  ), [updatePayload.settings]);
  // Heures par défaut des chauffeurs : relecture des Mise à jour camions, Jour et Soir.
  const truckSourceChangesToggleChanged = React.useMemo(() => (
    updatePayload.settings.some((setting) => DRIVER_DEFAULT_HOURS_SETTING_KEYS.has(setting?.key))
  ), [updatePayload.settings]);
  const canEdit = Boolean(state?.can_edit);
  const settingsBySection = React.useMemo(
    () => groupSettingsBySection(Array.isArray(state?.settings) ? state.settings : []),
    [state],
  );
  const orderedSettingSections = React.useMemo(
    () => Object.entries(settingsBySection),
    [settingsBySection],
  );
  const leftColumnSettingSections = React.useMemo(
    () => orderedSettingSections.filter(([sectionKey]) => sectionKey !== "solver_settings"),
    [orderedSettingSections],
  );
  const generalSettingSections = React.useMemo(
    () => leftColumnSettingSections.filter(([sectionKey]) => GENERAL_SETTING_SECTION_KEYS.has(sectionKey)),
    [leftColumnSettingSections],
  );
  const otherLeftColumnSettingSections = React.useMemo(
    () => leftColumnSettingSections.filter(([sectionKey]) => !GENERAL_SETTING_SECTION_KEYS.has(sectionKey)),
    [leftColumnSettingSections],
  );
  const rightColumnSettingSections = React.useMemo(
    () => orderedSettingSections.filter(([sectionKey, settings]) => (
      sectionKey === "solver_settings"
      && buildDisplaySettingGroups(sectionKey, settings).some(
        ([groupKey]) => ![PRE_ANCHOR_BUNDLE_GROUP_KEY, PRIORITY_TIME_WINDOWS_GROUP_KEY].includes(groupKey),
      )
    )),
    [orderedSettingSections],
  );
  const solverPrioritySettings = React.useMemo(() => {
    const solverSettings = Array.isArray(settingsBySection.solver_settings) ? settingsBySection.solver_settings : [];
    const priorityGroupEntry = buildDisplaySettingGroups("solver_settings", solverSettings).find(
      ([groupKey]) => groupKey === PRE_ANCHOR_BUNDLE_GROUP_KEY,
    );
    return Array.isArray(priorityGroupEntry?.[1]) ? priorityGroupEntry[1] : [];
  }, [settingsBySection]);
  const strictP2P3TimeWindowsSetting = React.useMemo(() => {
    const solverSettings = Array.isArray(settingsBySection.solver_settings) ? settingsBySection.solver_settings : [];
    return solverSettings.find((setting) => setting?.key === STRICT_P2_P3_TIME_WINDOWS_SETTING_KEY) || null;
  }, [settingsBySection]);
  const hasPrioritySection = Boolean(
    (Array.isArray(solverPrioritySettings) && solverPrioritySettings.length > 0)
    || strictP2P3TimeWindowsSetting
    || (Array.isArray(state?.time_window_tolerances) && state.time_window_tolerances.length > 0)
  );
  const jumpNavItems = React.useMemo(() => {
    const items = [];
    if (canManageSystemDiagnostics(access)) {
      items.push({ key: "diagnostic", title: "Diagnostic" });
    }
    // « Préférences d'affichage » est personnelle : « Réglages généraux » s'affiche pour toute personne qui ouvre la page.
    items.push({ key: "general", title: "Réglages généraux" });
    [...leftColumnSettingSections, ...rightColumnSettingSections].forEach(([sectionKey]) => {
      if (GENERAL_SETTING_SECTION_KEYS.has(sectionKey)) {
        return;
      }
      const sectionMeta = SECTION_META[sectionKey];
      items.push({
        key: sectionKey,
        title: sectionMeta?.title || sectionKey,
      });
    });
    if (hasPrioritySection) {
      items.push({ key: "priorities", title: "Paramètres liés aux priorités" });
    }
    items.push({ key: "history", title: "Historique récent" });
    return items;
  }, [
    access,
    hasPrioritySection,
    leftColumnSettingSections,
    rightColumnSettingSections,
  ]);

  const handleSettingChange = React.useCallback((key, nextValue) => {
    setDraft((currentDraft) => ({
      ...currentDraft,
      settings: {
        ...currentDraft.settings,
        [key]: nextValue,
      },
    }));
  }, []);

  const handleToleranceChange = React.useCallback((priority, field, nextValue) => {
    setDraft((currentDraft) => {
      const currentToleranceDraft = currentDraft.timeWindowTolerances[priority] || { before: "", after: "" };
      const nextToleranceDraft = {
        ...currentToleranceDraft,
        [field]: nextValue,
      };

      if (priority !== "P1") {
        const mirroredValue = field === "before" ? nextValue : nextToleranceDraft.before;
        nextToleranceDraft.before = mirroredValue;
        nextToleranceDraft.after = mirroredValue;
      }

      return {
        ...currentDraft,
        timeWindowTolerances: {
          ...currentDraft.timeWindowTolerances,
          [priority]: nextToleranceDraft,
        },
      };
    });
  }, []);

  const handleOptimizationAnimationChange = React.useCallback((nextValue) => {
    void Promise.resolve(setOptimizationAnimationEnabled(nextValue, { immediate: true })).catch(() => {});
  }, [setOptimizationAnimationEnabled]);

  const handleReload = React.useCallback(() => {
    setFeedback(null);
    void loadState();
  }, [loadState]);

  const handleSubmit = React.useCallback(async () => {
    if (!canEdit || !hasChanges || isSaving) {
      return;
    }

    setIsSaving(true);
    setFeedback(null);
    try {
      const response = await updateSystemConfigState(updatePayload);
      setState(response.state);
      setDraft(buildDraftFromState(response.state));
      if (runtimeAffectingSettingsChanged) {
        invalidatePageDataCacheEntries(["/api/results/latest"]);
        invalidateShellDataCache();
      }
      if (dispatchPeriodToggleChanged) {
        invalidatePageDataCacheEntries(["/api/results/latest"]);
        invalidateShellDataCache();
      }
      if (truckSourceChangesToggleChanged) {
        invalidatePageDataCacheEntries(["/api/results/latest"]);
        invalidateShellDataCache();
        dispatchPlanningStateChange("system-config", {
          actionName: "truck-source-changes-toggle",
          changeDomains: ["fleet"],
          dispatchPeriod: "",
          refreshSourceChangePeriods: ["day", "evening"],
        });
      }
      showToast({
        duration: 6000,
        title: "Paramètres système enregistrés",
        message: runtimeAffectingSettingsChanged
          ? "Paramètres système enregistrés. Le dispatch passe à revalider."
          : "Paramètres système enregistrés.",
        type: "success",
      });
      if ((updatePayload.settings || []).some((setting) => FIXED_TIME_SETTING_KEYS.has(setting.key))) {
        void refreshActiveDispatchWithToast({ showToast, updateToast });
      }
    } catch (error) {
      setFeedback({
        tone: "danger",
        message: error instanceof Error ? error.message : "Impossible d'enregistrer les paramètres système.",
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    canEdit,
    dispatchPeriodToggleChanged,
    hasChanges,
    isSaving,
    runtimeAffectingSettingsChanged,
    showToast,
    truckSourceChangesToggleChanged,
    updatePayload,
    updateToast,
  ]);

  if (isLoading && !state) {
    return (
      <SystemConfigPageFrame isPending>
        <section className="flex min-h-56 items-center justify-center rounded-[4px] border border-black/6 bg-white px-6 py-8 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
          <div className="flex items-center gap-3 text-slate-600">
            <LoaderCircle className="animate-spin" size={18} aria-hidden="true" />
            <span className="text-[0.94rem] font-medium">Chargement des paramètres système…</span>
          </div>
        </section>
      </SystemConfigPageFrame>
    );
  }

  if (!state) {
    return (
      <SystemConfigPageFrame isPending>
        <section className="grid gap-3 rounded-[4px] border border-border-danger-subtle bg-white px-6 py-6 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
          <div className="flex items-center gap-3 text-danger">
            <AlertTriangle size={18} aria-hidden="true" />
            <p className="m-0 text-[1rem] font-semibold text-slate-950">Paramètres indisponibles</p>
          </div>
          <p className="m-0 text-[0.92rem] leading-6 text-slate-600">{loadError || "La lecture des paramètres système a échoué."}</p>
          <div>
            <button className={SECONDARY_ACTION_CLASS_NAME} onClick={handleReload} type="button">
              <RefreshCcw size={15} aria-hidden="true" />
              Réessayer
            </button>
          </div>
        </section>
      </SystemConfigPageFrame>
    );
  }

  return (
    <SystemConfigPageFrame>
      <div className="sticky top-0 z-30 flex min-w-0 flex-wrap items-center gap-2 self-start rounded-[4px] border border-line-soft bg-white px-3 py-2 shadow-sm">
        <SettingSectionJumpNav items={jumpNavItems} />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button className={SECONDARY_ACTION_CLASS_NAME} onClick={handleReload} type="button">
            <RefreshCcw size={15} aria-hidden="true" />
            Recharger
          </button>
          <button
            className={PRIMARY_ACTION_CLASS_NAME}
            disabled={!canEdit || !hasChanges || isSaving}
            onClick={handleSubmit}
            type="button"
          >
            {isSaving ? <LoaderCircle className="animate-spin" size={15} aria-hidden="true" /> : <Save size={15} aria-hidden="true" />}
            Enregistrer
          </button>
        </div>
      </div>

      <section
        className={joinClasses([
          "grid gap-3 rounded-[4px] border border-black/6 bg-white px-4 py-3.5 shadow-[0_14px_34px_rgba(15,23,42,0.05)] empty:hidden max-[720px]:px-3.5",
          SYSTEM_CONFIG_SECTION_SCROLL_MARGIN_CLASS_NAME,
        ])}
      >
        {loadError ? (
          <div className={joinClasses(["rounded-[4px] px-3.5 py-3 text-[0.86rem] font-medium", resolveFeedbackToneClassName("danger")])}>
            {loadError}
          </div>
        ) : null}

        {feedback ? (
          <div className={joinClasses(["rounded-[4px] px-3.5 py-3 text-[0.86rem] font-medium", resolveFeedbackToneClassName(feedback.tone)])}>
            {feedback.message}
          </div>
        ) : null}

        {!canEdit ? (
          <div className={joinClasses(["rounded-[4px] px-3.5 py-3 text-[0.86rem] font-medium", resolveFeedbackToneClassName("info")])}>
            Votre rôle ne peut modifier aucun réglage.
          </div>
        ) : null}

      </section>

      <SystemDiagnosticsSection
        performanceCollectionState={state.performance_collection}
        sectionIdPrefix="system-config-section"
      />

      <div className="grid gap-3">
        <section className="grid gap-2.5 rounded-[4px] border border-black/6 bg-[linear-gradient(180deg,rgba(248,250,252,0.96),rgba(255,255,255,0.98))] px-3 py-3 shadow-[0_12px_28px_rgba(15,23,42,0.045)] max-[720px]:px-3">
          <div className="grid gap-2.5">
              <section
                aria-labelledby="system-config-general-title"
                className={joinClasses([
                  "grid gap-2.5 min-[1200px]:grid-cols-2 min-[1600px]:grid-cols-4 min-[1200px]:items-start",
                  SYSTEM_CONFIG_SECTION_SCROLL_MARGIN_CLASS_NAME,
                ])}
                data-general-settings-row="true"
                id={resolveSystemConfigSectionId("general")}
              >
                <h2 id="system-config-general-title" className="col-span-full m-0 text-[1rem] font-semibold tracking-[-0.025em] text-slate-950">
                  Réglages généraux
                </h2>
                {generalSettingSections.filter(([sectionKey]) => sectionKey !== "deployment_update").map(([sectionKey, settings]) => (
                  <SettingSectionCard
                    canEdit={canEdit}
                    key={sectionKey}
                    onChange={handleSettingChange}
                    sectionKey={sectionKey}
                    settings={settings}
                    values={draft.settings}
                  />
                ))}
                <OptimizationAnimationPreferenceCard
                  disabled={!preferencesUserId}
                  enabled={optimizationAnimationEnabled}
                  isSaving={isUserUiPreferenceSaving}
                  onChange={handleOptimizationAnimationChange}
                />
                {settingsBySection.deployment_update?.length > 0 ? (
                  <SettingSectionCard
                    canEdit={canEdit}
                    onChange={handleSettingChange}
                    sectionKey="deployment_update"
                    settings={settingsBySection.deployment_update}
                    values={draft.settings}
                  />
                ) : null}
              </section>
            {otherLeftColumnSettingSections.map(([sectionKey, settings]) => (
              <SettingSectionCard
                canEdit={canEdit}
                key={sectionKey}
                onChange={handleSettingChange}
                sectionKey={sectionKey}
                settings={settings}
                values={draft.settings}
              />
            ))}
          </div>
        </section>

        {rightColumnSettingSections.length > 0 ? (
          <section className="grid gap-2.5 rounded-[4px] border border-black/6 bg-[linear-gradient(180deg,rgba(248,250,252,0.96),rgba(255,255,255,0.98))] px-3 py-3 shadow-[0_12px_28px_rgba(15,23,42,0.045)] max-[720px]:px-3">
            <div className="grid gap-2.5">
              {rightColumnSettingSections.map(([sectionKey, settings]) => (
                <SettingSectionCard
                  canEdit={canEdit}
                  hiddenGroupKeys={sectionKey === "solver_settings"
                    ? [PRE_ANCHOR_BUNDLE_GROUP_KEY, PRIORITY_TIME_WINDOWS_GROUP_KEY]
                    : []}
                  key={sectionKey}
                  onChange={handleSettingChange}
                  sectionKey={sectionKey}
                  settings={settings}
                  values={draft.settings}
                />
              ))}
            </div>
          </section>
        ) : null}
      </div>

      <PrioritySettingsSection
        canEdit={canEdit}
        draftToleranceValues={draft.timeWindowTolerances}
        onSettingChange={handleSettingChange}
        onToleranceChange={handleToleranceChange}
        prioritySettings={solverPrioritySettings}
        settingValues={draft.settings}
        strictP2P3TimeWindowsSetting={strictP2P3TimeWindowsSetting}
        tolerances={state.time_window_tolerances || []}
      />

      <section
        className={joinClasses([
          "grid gap-2.5 rounded-[4px] border border-black/6 bg-white px-3 py-3 shadow-[0_12px_28px_rgba(15,23,42,0.045)] max-[720px]:px-3",
          SYSTEM_CONFIG_SECTION_SCROLL_MARGIN_CLASS_NAME,
        ])}
        id={resolveSystemConfigSectionId("history")}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <button
              type="button"
              onClick={() => setIsHistoryCollapsed((currentValue) => !currentValue)}
              aria-label={isHistoryCollapsed ? "Déplier l’historique récent" : "Replier l’historique récent"}
              className="inline-flex h-7 w-7 items-center justify-center rounded-[4px] border border-slate-200 bg-white text-slate-500 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950"
            >
              <ChevronRight size={13} className={isHistoryCollapsed ? "" : "rotate-90"} aria-hidden="true" />
            </button>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-[4px] bg-slate-100 text-slate-700">
              <History size={16} aria-hidden="true" />
            </span>
            <div className="grid gap-0.5">
              <p className="m-0 text-[0.95rem] font-semibold tracking-[-0.02em] text-slate-950">Historique récent</p>
              <p className="m-0 text-[0.8rem] leading-5 text-slate-600">Dernières modifications de paramètres et de tolérances.</p>
            </div>
          </div>

          <span className={joinClasses([
            "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
            pillToneClasses.outline,
          ])}>
            {Array.isArray(state.history) ? state.history.length : 0} entrée(s)
          </span>
        </div>

        {!isHistoryCollapsed ? (
          Array.isArray(state.history) && state.history.length > 0 ? (
            <ol className="grid gap-2.5">
              {state.history.map((entry) => (
                <li key={entry.audit_id} className="grid gap-1.5 rounded-[4px] border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="m-0 text-[0.82rem] font-semibold text-slate-900">{entry.entity_label || entry.entity_id}</p>
                  </div>
                  <p className="m-0 text-[0.78rem] leading-5 text-slate-600">{entry.summary || "Modification enregistrée."}</p>
                  <div className="flex flex-wrap items-center gap-2 text-[0.72rem] text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <Clock3 size={12} aria-hidden="true" />
                      {formatHistoryDate(entry.created_at)}
                    </span>
                    {entry.actor_email ? <span>• {entry.actor_email}</span> : null}
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <div className="rounded-[4px] border border-dashed border-slate-200 bg-slate-50 px-3.5 py-3 text-[0.84rem] leading-5 text-slate-600">
              Aucun changement récent n’a encore été journalisé pour cette surface.
            </div>
          )
        ) : null}
      </section>
    </SystemConfigPageFrame>
  );
}
